/**
 * Shared API client. Wraps the fetch pattern used across all pages:
 * same-origin cookies, JSON bodies, and server-provided error messages.
 */
import { apiUrl } from "./serverBase.js";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** GET a JSON resource. Throws ApiError(errorMessage) on a non-2xx response. */
export async function apiGet<T = any>(url: string, errorMessage = "Request failed"): Promise<T> {
  const res = await fetch(apiUrl(url), { credentials: "include" });
  if (!res.ok) throw new ApiError(errorMessage, res.status);
  return res.json() as Promise<T>;
}

async function send<T>(method: string, url: string, body: unknown, errorMessage: string): Promise<T> {
  const res = await fetch(apiUrl(url), {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const payload = data as { error?: string; errors?: Array<{ message?: string }> };
    throw new ApiError(payload.error ?? payload.errors?.[0]?.message ?? errorMessage, res.status);
  }
  return data as T;
}

/** POST a JSON body. Throws ApiError with the server's `error` field when available. */
export function apiPost<T = any>(url: string, body?: unknown, errorMessage = "Request failed"): Promise<T> {
  return send<T>("POST", url, body, errorMessage);
}

/** PUT a JSON body. Throws ApiError with the server's `error` field when available. */
export function apiPut<T = any>(url: string, body?: unknown, errorMessage = "Request failed"): Promise<T> {
  return send<T>("PUT", url, body, errorMessage);
}

/** PATCH a JSON body. Throws ApiError with the server's `error` field when available. */
export function apiPatch<T = any>(url: string, body?: unknown, errorMessage = "Request failed"): Promise<T> {
  return send<T>("PATCH", url, body, errorMessage);
}

/** DELETE a resource. Throws ApiError with the server's `error` field when available. */
export function apiDelete<T = any>(url: string, errorMessage = "Request failed"): Promise<T> {
  return send<T>("DELETE", url, undefined, errorMessage);
}
